<script>
import appConfig from '@/app.config';
import PageHeader from '@/components/page-header';
import { required } from 'vuelidate/lib/validators';
import Repositories from '@/repositories';
const DailyCapacitiesRepository = Repositories.get('dailycapacities');

export default {
    page: {
        title: 'projectmanagers',
        meta: [
            {
                name: 'description',
                content: appConfig.description,
            },
        ],
    },
    components: {
        PageHeader,
    },
    data() {
        return {
            title: 'Production',
            isLoading: false,
            dailyCapacities: [],
            capacityForm: {
                measureType: null,
                min: null,
                max: null,
            },
            capacitySubmit: false,
        };
    },
    created() {
        this.isLoading = true;

        DailyCapacitiesRepository.customREST
            .getProduction()
            .then((response) => {
                this.dailyCapacities = response.data;
            })
            .finally(() => {
                this.isLoading = false;
            });
    },
    mounted() {},
    methods: {
        submitCapacityForm() {
            this.capacitySubmit = true;
            this.$v.capacityForm.$touch();

            if (!this.$v.capacityForm.$invalid) {
                this.isLoading = true;
                let payload = {
                    min: this.capacityForm.min,
                    max: this.capacityForm.max,
                    measureType: this.capacityForm.measureType,
                };

                DailyCapacitiesRepository.create(payload)
                    .then((resp) => {
                        this.capacitySubmit = false;

                        this.dailyCapacities = [
                            ...this.dailyCapacities,
                            resp.data,
                        ];

                        this.capacityForm.measureType = null;
                        this.capacityForm.min = null;
                        this.capacityForm.max = null;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },
        deleteCapacity(capacity) {
            this.isLoading = true;
            DailyCapacitiesRepository.delete(capacity.id)
                .then(() => {
                    this.dailyCapacities = this.dailyCapacities.filter(
                        (x) => x.id != capacity.id
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
    computed: {
        measureTypes() {
            return [
                'SheetsQuantity',
                'NumericValue',
                'SquareFoot',
                'SquareMeter',
                'TimePerWorkOrder',
                'DoorsQuantity',
                'Products',
                'Parts',
            ];
        },
        measureTypesComputed() {
            const usedTypes = this.dailyCapacities.map(
                (cap) => cap.measureType
            );
            return this.measureTypes.filter(
                (type) => !usedTypes.includes(type)
            );
        },
    },
    validations: {
        capacityForm: {
            measureType: { required },
            min: { required },
            max: { required },
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="$t('menuitems.configuration.list.production')" />
        <b-card>
            <loading
                :active.sync="isLoading"
                :can-cancel="false"
                color="#f1b44c"
                :width="70"
                :height="70"
                loader="dots"
                :is-full-page="false"
            ></loading>

            <div class="col-12">
                <h4 class="mt-2 mb-2">{{ $t('general.dailyCapacities') }}</h4>

                <form class="mb-4" @submit.prevent="submitCapacityForm">
                    <div
                        class="row zno-gutters"
                        style="max-width:700px; align-items:flex-end;"
                    >
                        <div class="col-12 col-md-6 mt-2">
                            <div
                                class="form-group mb-0"
                                style="position:relative; top:2px;"
                            >
                                <label>{{ $t('general.capacity') }}</label>
                                <multiselect
                                    :validator="$v.capacityForm.measureType"
                                    :class="{
                                        'is-invalid':
                                            capacitySubmit &&
                                            $v.capacityForm.measureType.$error,
                                    }"
                                    style="width:100%;"
                                    v-model="capacityForm.measureType"
                                    :options="measureTypesComputed"
                                    :placeholder="
                                        $t('general.selectDailyCapacity')
                                    "
                                    :show-labels="false"
                                >
                                    <template
                                        slot="singleLabel"
                                        slot-scope="props"
                                    >
                                        <span>{{
                                            $t('general.' + props.option)
                                        }}</span>
                                    </template>
                                    <template slot="option" slot-scope="props">
                                        <span>{{
                                            $t('general.' + props.option)
                                        }}</span>
                                    </template>
                                </multiselect>
                                <div
                                    v-if="
                                        capacitySubmit &&
                                            $v.capacityForm.measureType.$error
                                    "
                                    class="invalid-tooltip"
                                >
                                    <span
                                        v-if="
                                            !$v.capacityForm.measureType
                                                .required
                                        "
                                        >{{ $t('general.requiredValue') }}</span
                                    >
                                </div>
                            </div>
                        </div>

                        <div class="col-12 col-md-2 mt-2">
                            <b-form-group
                                class="mb-0"
                                style="position:relative;"
                            >
                                <label>{{ $t('general.min') }}</label>
                                <b-form-input
                                    type="number"
                                    step="0.01"
                                    :class="{
                                        'is-invalid':
                                            capacitySubmit &&
                                            $v.capacityForm.min.$error,
                                    }"
                                    style="max-width:90px;"
                                    v-model="capacityForm.min"
                                    placeholder="Min"
                                ></b-form-input>
                                <div
                                    v-if="
                                        capacitySubmit &&
                                            $v.capacityForm.min.$error
                                    "
                                    class="invalid-tooltip"
                                >
                                    <span
                                        v-if="!$v.capacityForm.min.required"
                                        >{{ $t('general.requiredValue') }}</span
                                    >
                                </div>
                            </b-form-group>
                        </div>

                        <div class="col-12 col-md-2 mt-2">
                            <b-form-group
                                class="mb-0"
                                style="position:relative;"
                            >
                                <label>{{ $t('general.max') }}</label>
                                <b-form-input
                                    type="number"
                                    step="0.01"
                                    :class="{
                                        'is-invalid':
                                            capacitySubmit &&
                                            $v.capacityForm.max.$error,
                                    }"
                                    style="max-width:90px;"
                                    v-model="capacityForm.max"
                                    placeholder="Max"
                                ></b-form-input>
                                <div
                                    v-if="
                                        capacitySubmit &&
                                            $v.capacityForm.max.$error
                                    "
                                    class="invalid-tooltip"
                                >
                                    <span
                                        v-if="!$v.capacityForm.max.required"
                                        >{{ $t('general.requiredValue') }}</span
                                    >
                                </div>
                            </b-form-group>
                        </div>

                        <div class="col-12 col-md-2 mt-2">
                            <b-button
                                style="height:36px;"
                                type="submit"
                                variant="primary"
                                >{{ $t('general.add') }}</b-button
                            >
                        </div>
                    </div>
                </form>
                <div class="table-responsive">
                    <table class="table mb-0">
                        <thead class="thead-light">
                            <tr>
                                <th>{{ $t('general.capacity') }}</th>
                                <th style="text-align:center;">{{
                                    $t('general.min')
                                }}</th>
                                <th style="text-align:center;">
                                    {{ $t('general.max') }}
                                </th>
                                <th style="width:100px; text-align:center;">{{
                                    $t('general.delete')
                                }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(capacity, index) in dailyCapacities"
                                :key="index"
                            >
                                <td>
                                    {{ $t('general.' + capacity.measureType) }}
                                </td>
                                <td style="text-align:center;">
                                    {{ capacity.min }}
                                </td>
                                <td style="text-align:center;">
                                    {{ capacity.max }}
                                </td>
                                <td
                                    @click="deleteCapacity(capacity)"
                                    style="text-align:center; color: #f46a6a; font-size:1.8em;"
                                    ><i
                                        style="cursor:pointer;"
                                        class="mdi mdi-trash-can-outline"
                                    ></i
                                ></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </b-card>
    </Layout>
</template>
